import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { STATUTS } from './Takata';

const CONSTRUCTEURS = ['Chevrolet', 'Ford', 'Honda', 'Mazda', 'Nissan', 'Opel', 'Skoda', 'Suzuki'];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function PortailComptesAdmin() {
  const [comptes, setComptes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editCompte, setEditCompte] = useState(null);

  const loadComptes = useCallback(() => {
    setLoading(true);
    api.get('/portail-constructeur/comptes')
      .then(r => setComptes(r.data.comptes))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadComptes(); }, [loadComptes]);

  const handleDeactivate = async (id) => {
    if (!window.confirm('Désactiver ce compte ?')) return;
    await api.delete(`/portail-constructeur/comptes/${id}`);
    loadComptes();
  };

  const handleReactivate = async (id, compte) => {
    await api.put(`/portail-constructeur/comptes/${id}`, {
      nom: compte.nom,
      marques: compte.marques,
      statuts_visibles: compte.statuts_visibles,
      actif: true,
    });
    loadComptes();
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          Comptes d'accès au portail constructeur (
          <code style={{ fontSize: '0.85rem' }}>/portail-constructeur</code>
          ) — lecture seule des dossiers Takata
        </div>
        <button
          className="page-action-btn"
          style={{ padding: '8px 16px', fontSize: '0.88rem' }}
          onClick={() => { setEditCompte(null); setShowModal(true); }}
        >
          + Nouveau compte
        </button>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Identifiant</th>
              <th>Marques</th>
              <th>Statuts visibles</th>
              <th>Dernière connexion</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>
            ) : comptes.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Aucun compte portail</td></tr>
            ) : comptes.map(c => (
              <tr key={c.id} style={{ opacity: c.actif ? 1 : 0.5 }}>
                <td style={{ fontWeight: 600 }}>{c.nom || '—'}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.88rem' }}>{c.username}</td>
                <td style={{ fontSize: '0.85rem' }}>{(c.marques || []).join(', ') || '—'}</td>
                <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                  {c.statuts_visibles?.length
                    ? c.statuts_visibles.map(s => STATUTS.find(x => x.value === s)?.label || s).join(', ')
                    : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Tous (hors réparé/clos)</span>}
                </td>
                <td style={{ fontSize: '0.85rem' }}>{formatDate(c.derniere_connexion)}</td>
                <td>
                  <span style={{
                    background: c.actif ? '#22c55e22' : '#94a3b822',
                    color: c.actif ? 'light-dark(#22c55e, var(--color-success-dark))' : '#94a3b8',
                    padding: '2px 8px', borderRadius: 5, fontSize: '0.8rem', fontWeight: 600,
                  }}>
                    {c.actif ? 'Actif' : 'Désactivé'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon" title="Modifier" onClick={() => { setEditCompte(c); setShowModal(true); }}>✏️</button>
                    {c.actif
                      ? <button className="btn-icon delete" title="Désactiver" onClick={() => handleDeactivate(c.id)}>🚫</button>
                      : <button className="btn-icon" title="Réactiver" onClick={() => handleReactivate(c.id, c)}>✅</button>
                    }
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <PortailCompteModal
          compte={editCompte}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); loadComptes(); }}
        />
      )}
    </div>
  );
}

function PortailCompteModal({ compte, onClose, onSaved }) {
  const isEdit = !!compte;
  const [form, setForm] = useState({
    username:         compte?.username || '',
    password:         '',
    nom:              compte?.nom || '',
    marques:          compte?.marques || [],
    statuts_visibles: compte?.statuts_visibles || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleArr = (key, val) => {
    setForm(f => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
    }));
  };

  const handleSave = async () => {
    setError('');
    if (!form.username) return setError('Identifiant requis');
    if (!isEdit && !form.password) return setError('Mot de passe requis');
    if (!form.marques.length) return setError('Au moins une marque requise');

    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          nom: form.nom,
          marques: form.marques,
          statuts_visibles: form.statuts_visibles,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/portail-constructeur/comptes/${compte.id}`, payload);
      } else {
        await api.post('/portail-constructeur/comptes', {
          username: form.username,
          password: form.password,
          nom: form.nom,
          marques: form.marques,
          statuts_visibles: form.statuts_visibles,
        });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Modifier le compte' : 'Nouveau compte portail'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && (
            <div style={{ background: 'var(--color-error-light)', color: 'light-dark(var(--color-error), var(--color-error-dark))', padding: '8px 12px', borderRadius: 6, fontSize: '0.88rem' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Identifiant *</label>
            <input
              style={{ padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: '0.9rem', background: 'var(--bg-input)' }}
              value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              disabled={isEdit}
              placeholder="ex: nissan-takata"
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              {isEdit ? 'Nouveau mot de passe (vide = inchangé)' : 'Mot de passe *'}
            </label>
            <input
              type="password"
              style={{ padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: '0.9rem', background: 'var(--bg-input)' }}
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder={isEdit ? '••••••••' : ''}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Nom affiché</label>
            <input
              style={{ padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: '0.9rem', background: 'var(--bg-input)' }}
              value={form.nom}
              onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
              placeholder="ex: Nissan France"
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Marques accessibles *</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
              {CONSTRUCTEURS.map(c => (
                <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.marques.includes(c.toUpperCase())}
                    onChange={() => toggleArr('marques', c.toUpperCase())}
                  />
                  {c}
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              Statuts visibles{' '}
              <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(vide = tout sauf réparé/clos)</span>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
              {STATUTS.map(s => (
                <label key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.statuts_visibles.includes(s.value)}
                    onChange={() => toggleArr('statuts_visibles', s.value)}
                  />
                  {s.label}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer le compte'}
          </button>
        </div>
      </div>
    </div>
  );
}
